import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { api } from "../shared/utilities/api";
import apiConfigs from "../shared/utilities/apiConfigs";

const ORANGE = "#EA8331";
const ORANGE_DARK = "#C96820";

const OPTIONS = [
	{ value: 0, label: "Never" },
	{ value: 2, label: "2 days before" },
	{ value: 7, label: "7 days before" },
];

// ─── Lightweight responsive overrides — only for breakpoints inline styles can't handle ──
const injectResponsiveStyles = () => {
	if (document.getElementById("patient-settings-responsive")) return;
	const style = document.createElement("style");
	style.id = "patient-settings-responsive";
	style.textContent = `
		.ps-wrap {
			max-width: 100%;
			overflow-x: hidden;
		}
		.ps-footer {
			min-width: 0;
		}
		.ps-footer-hint {
			min-width: 0;
		}

		@media (max-width: 480px) {
			.ps-footer {
				flex-direction: column;
				align-items: stretch !important;
				gap: 10px !important;
			}
			.ps-footer-hint {
				white-space: normal !important;
			}
			.ps-save-btn {
				width: 100%;
			}
			.ps-chips {
				gap: 6px !important;
			}
		}
	`;
	document.head.appendChild(style);
};

const PatientSettings = ({ patient }) => {
	const { orgId } = useParams();

	const [patientReminderPreference, setPatientReminderPreference] = useState({
		reminderDaysBefore: 0,
	});
	const [reminderPref, setReminderPref] = useState(0);
	const [hoveredChip, setHoveredChip] = useState(null);
	const [showToast, setShowToast] = useState(false);
	const [saveHover, setSaveHover] = useState(false);

	const savedValue = patientReminderPreference.reminderDaysBefore;
	const hasChanged = reminderPref !== savedValue;

	const getLabelFor = (val) =>
		OPTIONS.find((o) => o.value === val)?.label ?? "Never";

	useEffect(() => {
		injectResponsiveStyles();
	}, []);

	const fetchPatientPreferences = async (orgId, demographicNo) => {
		try {
			const result = await api(
				apiConfigs.baseOption(
					"get",
					`patientReminderPreference/${orgId}/${demographicNo}`
				)
			);
			if (result?.data?.demographicNo === demographicNo) {
				setPatientReminderPreference(result.data);
				setReminderPref(result.data.reminderDaysBefore);
			} else {
				setReminderPref(0);
			}
		} catch (error) {
			console.error("Error retrieving preferences:", error);
		}
	};

	const savePatientPreferences = async () => {
		try {
			await api(
				apiConfigs.baseOption("post", `patientReminderPreference/`, {
					data: {
						orgId,
						demographicNo: patient.demographicNo,
						reminderDaysBefore: reminderPref,
					},
				})
			);
			setPatientReminderPreference((prev) => ({
				...prev,
				reminderDaysBefore: reminderPref,
			}));
			setShowToast(true);
			setTimeout(() => setShowToast(false), 2500);
		} catch (error) {
			console.error("Error saving preferences:", error);
		}
	};

	useEffect(() => {
		if (patient?.demographicNo) {
			fetchPatientPreferences(orgId, patient.demographicNo);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orgId, patient?.demographicNo]);

	// ── Chip style ──────────────────────────────────────────────
	const chipStyle = (val) => {
		const isSelected = reminderPref === val;
		const isHovered = hoveredChip === val && !isSelected;
		return {
			padding: "9px 18px",
			borderRadius: 50,
			fontSize: 13,
			cursor: "pointer",
			border: `1.5px solid ${isSelected ? ORANGE : isHovered ? ORANGE : "#D1D5DB"}`,
			background: isSelected ? ORANGE : "transparent",
			color: isSelected ? "#FFFFFF" : isHovered ? ORANGE : "#6B7280",
			fontWeight: isSelected ? 500 : 400,
			fontFamily: "Roboto, sans-serif",
			userSelect: "none",
			transition: "all 0.15s",
			whiteSpace: "nowrap",
		};
	};

	// ── Save button style ───────────────────────────────────────
	const saveBtnStyle = {
		padding: "9px 22px",
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 500,
		fontFamily: "Roboto, sans-serif",
		border: hasChanged ? "none" : "0.5px solid #E5E7EB",
		background: hasChanged
			? saveHover
				? ORANGE_DARK
				: ORANGE
			: "#FFFFFF",
		color: hasChanged ? "#FFFFFF" : "#9CA3AF",
		cursor: hasChanged ? "pointer" : "not-allowed",
		whiteSpace: "nowrap",
		flexShrink: 0,
		transition: "background 0.15s",
	};

	return (
		<div
			className="ps-wrap"
			style={{
				background: "#FFFFFF",
				border: "0.5px solid #E5E7EB",
				borderRadius: 16,
				overflow: "hidden",
			}}
		>
			{/* ── Header ── */}
			<div style={{ padding: "20px 20px 0" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						marginBottom: 5,
						flexWrap: "wrap",
					}}
				>
					{/* Bell icon box */}
					<div
						style={{
							width: 38,
							height: 38,
							borderRadius: 10,
							background: "#FEF0E4",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke={ORANGE}
							strokeWidth="1.8"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
							<path d="M13.73 21a2 2 0 0 1-3.46 0" />
						</svg>
					</div>

					<div style={{ minWidth: 0 }}>
						<div
							style={{
								fontSize: 15,
								fontWeight: 500,
								color: "#111827",
								fontFamily: "Roboto, sans-serif",
							}}
						>
							Appointment Reminders
						</div>
						<div
							style={{
								fontSize: 13,
								color: "#6B7280",
								marginTop: 2,
								fontFamily: "Roboto, sans-serif",
							}}
						>
							Extra notification before this patient's visit
						</div>
					</div>
				</div>

				{/* Current state pill */}
				<div
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 5,
						marginTop: 10,
						background: "#F3F4F6",
						border: "0.5px solid #E5E7EB",
						borderRadius: 20,
						padding: "4px 10px 4px 6px",
						maxWidth: "100%",
					}}
				>
					<div
						style={{
							width: 7,
							height: 7,
							borderRadius: "50%",
							background: ORANGE,
							flexShrink: 0,
						}}
					/>
					<span
						style={{
							fontSize: 12,
							color: "#6B7280",
							fontFamily: "Roboto, sans-serif",
						}}
					>
						Currently set to
					</span>
					<span
						style={{
							fontSize: 12,
							fontWeight: 500,
							color: "#111827",
							fontFamily: "Roboto, sans-serif",
						}}
					>
						{getLabelFor(savedValue)}
					</span>
				</div>
			</div>

			{/* ── Divider ── */}
			<div
				style={{
					height: "0.5px",
					background: "#E5E7EB",
					margin: "16px 0 0",
				}}
			/>

			{/* ── Chip selector ── */}
			<div style={{ padding: "16px 20px" }}>
				<div
					style={{
						fontSize: 11,
						textTransform: "uppercase",
						letterSpacing: "0.07em",
						color: "#9CA3AF",
						fontWeight: 500,
						marginBottom: 10,
						fontFamily: "Roboto, sans-serif",
					}}
				>
					Send reminder
				</div>
				<div className="ps-chips" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
					{OPTIONS.map(({ value, label }) => (
						<div
							key={value}
							style={chipStyle(value)}
							onClick={() => setReminderPref(value)}
							onMouseEnter={() => setHoveredChip(value)}
							onMouseLeave={() => setHoveredChip(null)}
						>
							{label}
						</div>
					))}
				</div>
			</div>

			{/* ── Footer ── */}
			<div
				className="ps-footer"
				style={{
					padding: "14px 20px",
					background: "#F9FAFB",
					borderTop: "0.5px solid #E5E7EB",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 12,
				}}
			>
				{/* Toast or hint */}
				{showToast ? (
					<div
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: 6,
							fontSize: 12,
							color: "#1A7A4A",
							background: "#EAF7F0",
							border: "0.5px solid #A8DFC0",
							borderRadius: 8,
							padding: "6px 12px",
						}}
					>
						<svg
							width="13"
							height="13"
							viewBox="0 0 24 24"
							fill="none"
							stroke="#1A7A4A"
							strokeWidth="2.5"
							aria-hidden="true"
						>
							<path d="M20 6L9 17l-5-5" />
						</svg>
						Preference saved
					</div>
				) : (
					<div
						className="ps-footer-hint"
						style={{
							fontSize: 12,
							color: "#9CA3AF",
							fontFamily: "Roboto, sans-serif",
							lineHeight: 1.4,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{hasChanged ? (
							<>
								Changing from{" "}
								<strong style={{ color: "#6B7280" }}>
									{getLabelFor(savedValue)}
								</strong>{" "}
								to{" "}
								<strong style={{ color: "#6B7280" }}>
									{getLabelFor(reminderPref)}
								</strong>
							</>
						) : (
							"No unsaved changes"
						)}
					</div>
				)}

				<button
					className="ps-save-btn"
					disabled={!hasChanged}
					onClick={savePatientPreferences}
					style={saveBtnStyle}
					onMouseEnter={() => setSaveHover(true)}
					onMouseLeave={() => setSaveHover(false)}
				>
					Save changes
				</button>
			</div>
		</div>
	);
};

export default PatientSettings;